/**
 * Agent edits in flight (user test fix, 2026-09-25): an `updatePage`,
 * `updateDocument` or `editImage` call from the live chat stream, from its
 * tool call until the new version is the artifact's head. The canvas panel,
 * the lightbox and the cards read it so an open file says "Editing" and then
 * shows the new version without a refresh:
 *
 * - `running`: the call has no result yet;
 * - `saved`: the result named the new version (`versionId`, `versionNumber`);
 *   a page or a document is saved already and waits for the head to be
 *   read again, an image version is still being made;
 * - removed when the head reaches the version (the `artifact:update` nudge
 *   or a detail refetch), when the call failed, or when the reply ended
 *   without a result.
 *
 * Module state like the draft store, because the chat runtime (outside
 * React) writes it and the panel, drawn by the shell, reads it. Nothing is
 * persisted. Suggest mode changes nothing and is never tracked.
 */
import { useEffect, useSyncExternalStore } from 'react';
import type { AgentArtifactKind } from '@tabbio/types';
import { artifactEditKind, isArtifactEditCall } from './artifact-model';

export type ArtifactEditActivity = {
  toolCallId: string;
  toolName: string;
  artifactId: string;
  kind: AgentArtifactKind;
  phase: 'running' | 'saved';
  versionId: string | null;
  versionNumber: number | null;
  startedAt: number;
};

/** An edit nobody settled is dropped after this long (a lost nudge, a closed tab). */
export const ARTIFACT_EDIT_ACTIVITY_TTL_MS = 5 * 60 * 1000;

const byCall = new Map<string, ArtifactEditActivity>();
const listeners = new Set<() => void>();
/** A stable snapshot per artifact, so `useSyncExternalStore` sees no change when nothing changed. */
let snapshot = new Map<string, ArtifactEditActivity>();

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function publish() {
  const next = new Map<string, ArtifactEditActivity>();
  for (const entry of byCall.values()) {
    const current = next.get(entry.artifactId);
    // The newest edit of an artifact wins its slot.
    if (!current || current.startedAt <= entry.startedAt) next.set(entry.artifactId, entry);
  }
  snapshot = next;
  for (const listener of [...listeners]) listener();
}

function expire(now: number) {
  let changed = false;
  for (const [toolCallId, entry] of byCall) {
    if (now - entry.startedAt > ARTIFACT_EDIT_ACTIVITY_TTL_MS) {
      byCall.delete(toolCallId);
      changed = true;
    }
  }
  return changed;
}

/** An edit call arrived on the live stream (`tool-call`). */
export function noteArtifactEditCall(
  toolCallId: string,
  toolName: string,
  args: unknown,
  now = Date.now(),
): ArtifactEditActivity | null {
  if (!toolCallId || !isArtifactEditCall(toolName, args)) return null;
  const artifactId = record(args)?.artifactId;
  if (typeof artifactId !== 'string' || !artifactId) return null;
  expire(now);
  const existing = byCall.get(toolCallId);
  if (existing) return existing;
  const entry: ArtifactEditActivity = {
    toolCallId,
    toolName,
    artifactId,
    kind: artifactEditKind(toolName),
    phase: 'running',
    versionId: null,
    versionNumber: null,
    startedAt: now,
  };
  byCall.set(toolCallId, entry);
  publish();
  return entry;
}

/**
 * The call's result arrived. A failure (or a result naming no version) ends
 * the edit; a saved version waits for the head to reach it.
 */
export function noteArtifactEditResult(toolCallId: string, result: unknown, failed: boolean) {
  const entry = byCall.get(toolCallId);
  if (!entry) return;
  const source = record(result);
  const versionId = typeof source?.versionId === 'string' && source.versionId ? source.versionId : null;
  const versionNumber =
    typeof source?.versionNumber === 'number' && source.versionNumber > 0 ? source.versionNumber : null;
  // A result that names its saved version is a success whatever the call's
  // status said (an older server marked a page kept with findings failed).
  const saved = source?.ok !== false && !source?.error && (versionId !== null || versionNumber !== null);
  if (!saved || (failed && versionId === null)) {
    byCall.delete(toolCallId);
    publish();
    return;
  }
  byCall.set(toolCallId, { ...entry, phase: 'saved', versionId, versionNumber });
  publish();
}

/**
 * The artifact's head is known (a nudge or a detail read): edits whose
 * version the head reached are over, and so is an image edit whose version
 * failed (`failedVersionId`).
 */
export function settleArtifactEdits(
  artifactId: string,
  head: { id?: string | null; number?: number | null } | null | undefined,
  options: { failedVersionId?: string | null } = {},
) {
  let changed = false;
  for (const [toolCallId, entry] of byCall) {
    if (entry.artifactId !== artifactId || entry.phase !== 'saved') continue;
    const reached =
      (entry.versionId !== null && entry.versionId === head?.id) ||
      (entry.versionNumber !== null && typeof head?.number === 'number' && head.number >= entry.versionNumber) ||
      (entry.versionId !== null && entry.versionId === options.failedVersionId);
    if (reached) {
      byCall.delete(toolCallId);
      changed = true;
    }
  }
  if (changed) publish();
}

/** The reply ended: calls that never got a result will not get one. */
export function stopArtifactEdits(toolCallIds: Iterable<string>) {
  let changed = false;
  for (const toolCallId of toolCallIds) {
    const entry = byCall.get(toolCallId);
    if (entry?.phase === 'running') {
      byCall.delete(toolCallId);
      changed = true;
    }
  }
  if (changed) publish();
}

export function readArtifactEditFor(artifactId: string | null | undefined): ArtifactEditActivity | null {
  if (!artifactId) return null;
  const entry = snapshot.get(artifactId) ?? null;
  if (entry && Date.now() - entry.startedAt > ARTIFACT_EDIT_ACTIVITY_TTL_MS) return null;
  return entry;
}

export function subscribeArtifactEdits(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const serverSnapshot = () => null;

/** The newest agent edit of one artifact that is not over yet, or null. */
export function useArtifactEditFor(artifactId: string | null | undefined): ArtifactEditActivity | null {
  return useSyncExternalStore(subscribeArtifactEdits, () => readArtifactEditFor(artifactId), serverSnapshot);
}

/**
 * A panel or a lightbox that read the head settles the edits that reached
 * it, and an image edit whose version failed (`failedVersionId`).
 */
export function useSettleArtifactEdits(
  artifactId: string | null | undefined,
  head: { id?: string | null; number?: number | null } | null | undefined,
  failedVersionId?: string | null,
) {
  const headId = head?.id ?? null;
  const headNumber = head?.number ?? null;
  useEffect(() => {
    if (!artifactId) return;
    if (headId || headNumber !== null || failedVersionId) {
      settleArtifactEdits(artifactId, { id: headId, number: headNumber }, { failedVersionId });
    }
  }, [artifactId, failedVersionId, headId, headNumber]);
}

export function resetArtifactEditsForTests() {
  byCall.clear();
  snapshot = new Map();
  listeners.clear();
}
